import React, { useState } from "react";
import { TbSearch, TbExternalLink } from "react-icons/tb";
import { useLatestScans } from "../../hooks/useReviewSchedule";
import { useSort } from "../../hooks/useSort";
import { Alert, EmptyState, TopbarSlot, SortHeader } from "../../components";
import { fmtDate } from "../../lib/dates";
import { STATE_LABEL } from "../../lib/ratings";
import "./Reviews.css";

const STATE_TONE = { ready: "warning", presented: "success" };

// Scanner results, reached from the review side.
//
// Nothing new is stored for this screen. The Review column is the only join: everything
// else is already on the scan record.
const SCAN_DATE_KEYS = ["scanned_at"];

const LatestScansScreen = ({ onNavigate, reviewOrgId, reviewOrgName, onClearReviewOrg }) => {
  const [search, setSearch] = useState("");
  const { scans, loading, error } = useLatestScans(search, reviewOrgId);

  // Most recent scan first, which is what the screen is called: the server already orders
  // them that way, so the initial sort agrees rather than reshuffling on first paint.
  const { sort, toggleSort, sorted } = useSort(scans, "scanned_at", SCAN_DATE_KEYS, "desc");

  return (
    <div className="v2-page">
      <TopbarSlot>
        <div className="v2-omni">
          <TbSearch size={14} />
          <input type="search" value={search} placeholder="Search organization or domain"
                 aria-label="Search organization or domain"
                 onChange={(e) => setSearch(e.target.value)} />
        </div>
      </TopbarSlot>

      <div className="v2-pagehead">
        <div>
          <h2>Latest scans</h2>
          <p>The most recent external scan per organization, with the review that goes with it.</p>
        </div>
      </div>

      <Alert>{error}</Alert>

      {loading ? <p className="v2-hint">Loading…</p>
        : scans.length === 0 ? (
          /* "No scans yet" is a claim about the whole workspace. When the organization
             filter is on it is also false — there may be plenty of scans, just none for
             the one organization being shown. Say which, and offer the way out. */
          reviewOrgId ? (
            <EmptyState title={`No scans for ${reviewOrgName || "this organization"}`}
              action={
                <button type="button" className="v2-btn v2-btn--primary" style={{ marginTop: ".75rem" }}
                        onClick={onClearReviewOrg}>
                  Show all organizations
                </button>
              }>
              Business reviews is filtered to {reviewOrgName || "one organization"}. Other
              organizations may still have scans.
            </EmptyState>
          ) : (
            <EmptyState title="No scans yet"
              action={onNavigate ? (
                <button type="button" className="v2-btn v2-btn--primary" style={{ marginTop: ".75rem" }}
                        onClick={() => onNavigate("scan")}>
                  Run an external risk scan
                </button>
              ) : null}>
              {search ? `Nothing matches "${search}".`
                      : "An external risk scan is what pre-rates the items a review cannot judge by eye."}
            </EmptyState>
          )
        ) : (
          <div className="v2-tablewrap">
            <table className="v2-table">
              <thead>
                <tr>
                  <SortHeader label="Organization" colKey="organization_name" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Domain" colKey="domain" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Score" colKey="score" sort={sort} onSort={toggleSort} className="v2-num" />
                  <SortHeader label="Scanned" colKey="scanned_at" sort={sort} onSort={toggleSort} />
                  {/* A link or a button, not a value to order rows by. */}
                  <th>Review</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((s) => (
                  <tr key={s.scan_id}>
                    <td>{s.organization_name}</td>
                    <td className="v2-dim">{s.domain || "—"}</td>
                    <td className="v2-num">{s.score === null ? "." : s.score}</td>
                    <td className="v2-dim">{fmtDate(s.scanned_at)}</td>
                    <td>
                      {s.review ? (
                        <span className={`v2-rbadge${STATE_TONE[s.review.state] ? ` v2-rbadge--${STATE_TONE[s.review.state]}` : ""}`}>
                          {(STATE_LABEL[s.review.state] || s.review.state).toUpperCase()}
                        </span>
                      ) : (
                        <span className="v2-rbadge">NO REVIEW YET</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <p className="v2-editor-foot">
        The Review column is the only new join. Everything else is already on the scan
        record, so this screen is a query away rather than a schema change.
        {" "}
        <TbExternalLink size={12} style={{ verticalAlign: "-2px" }} /> Full findings live
        in the External Risk Scanner.
      </p>
    </div>
  );
};

export default LatestScansScreen;
